import { useCallback, useEffect, useRef, useState } from "react";
import { dashboardGet } from "../../../api";
import useLiveData from "../../../realtime/useLiveData";

/**
 * Loads one dashboard endpoint.
 *
 * - Keeps the previous data on screen while new params load (`refreshing`), so
 *   changing a filter or a live update never flashes the skeleton again.
 * - A slower, older response can never overwrite a newer one.
 * - `live` topics (see realtime/useLiveData) refresh it when other tills change data.
 * - `fetcher` replaces the default dashboard call with a custom (params) => promise.
 * - `enabled: false` does nothing (used when the user has no access to the section).
 * - `liveMs`: the shortest gap between two live reloads (default 10 s; a screen that must feel
 *   instant, like Batch Stock, asks for 2 s).
 * - `midnight: true`: reload just after local midnight - days-left and Expired / Near expiry
 *   change with the calendar even when no stock moved.
 * - `refreshOnFocus: true`: reload when the tab becomes visible again after being hidden for a
 *   while (a till left open overnight, a laptop woken from sleep).
 */
export default function useDash(path, params, { enabled = true, live = [], fetcher, liveMs = 10000, midnight = false, refreshOnFocus = false } = {}) {
  const [state, setState] = useState({ data: null, status: enabled ? "loading" : "idle", error: null, at: null });
  const key = JSON.stringify(params || {});
  const seq = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) return;
    const id = ++seq.current;
    setState((s) => ({ ...s, status: s.data ? "refreshing" : "loading", error: null }));
    try {
      // `fetcher(params)` lets a widget page through another endpoint (e.g. the expiry report).
      const { data } = fetcher ? await fetcher(JSON.parse(key)) : await dashboardGet(path, JSON.parse(key));
      if (id !== seq.current) return;
      setState({ data, status: "ready", error: null, at: new Date() });
    } catch (error) {
      if (id !== seq.current) return;
      setState((s) => ({ ...s, status: "error", error: error?.response?.data?.message || error.message || "Could not load" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  useLiveData(live.length ? live : ["__none__"], () => load(), { minIntervalMs: liveMs, enabled: enabled && live.length > 0 });

  // Calendar change: statuses depend on today's date, so re-read a few seconds after midnight.
  useEffect(() => {
    if (!midnight || !enabled) return undefined;
    let timer;
    const arm = () => {
      const now = new Date();
      const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 5);
      timer = setTimeout(() => {
        load();
        arm();
      }, Math.max(1000, next - now));
    };
    arm();
    return () => clearTimeout(timer);
  }, [midnight, enabled, load]);

  // Back on the tab after a while away (or the laptop slept past midnight): refresh once.
  const lastAtRef = useRef(Date.now());
  lastAtRef.current = state.at ? state.at.getTime() : lastAtRef.current;
  useEffect(() => {
    if (!refreshOnFocus || !enabled) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastAtRef.current > 60000) load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [refreshOnFocus, enabled, load]);

  return {
    data: state.data,
    error: state.error,
    at: state.at,
    reload: load,
    loading: state.status === "loading",
    refreshing: state.status === "refreshing",
    failed: state.status === "error",
  };
}
